import { AppForm } from '../../../../../../common/forms/app.form';
import { Injectable } from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import {
  RoadOccupationStageForm,
  RoadOccupationStageFormValue, toStageFormValue
} from './road-occupation-stage.form';
import { RoadOccupationAppEntityApplicationRead } from '../../../../../../openapi';
import { CustomValidator } from '../../../../../../common/forms/custom.validators';

export function occupationsToFormValue(occupations: Array<RoadOccupationAppEntityApplicationRead>): Array<RoadOccupationFormValue> {
  return occupations.map(v => {
    return {
      stages: v?.stages?.map(v => toStageFormValue(v)) || [],
      id: v?.uuid || '',
      occupationType: v?.occupationType?.id,
      purpose: v?.purpose,
      locationDecisionDate: v?.locationDecisionDate || null,
      locationDecisionNumber: v?.locationDecisionNumber || '',
      agreementDate: v?.agreementDate || null,
      agreementNumber: v?.agreementNumber || '',
      surfaceViolation: v?.surfaceViolation,
      workPurpose: v?.workPurpose,
      deviceType: v?.deviceType
    };
  });
}

export interface RoadOccupationFormValue {
  id: string;
  occupationType?: string;
  surfaceViolation?: string;
  purpose?: string;
  workPurpose?: string;
  deviceType?: string;
  locationDecisionNumber: string;
  locationDecisionDate: Date | null;
  agreementNumber: string | null;
  agreementDate: Date | null;
  stages: RoadOccupationStageFormValue[];
}

@Injectable()
export class RoadOccupationForm extends AppForm {

  public locationDecisionMaxDate: Date = new Date();

  constructor() {
    super({
      id: new FormControl('', { nonNullable: true }),
      occupationType: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      purpose: new FormControl('', { validators: [Validators.required, Validators.maxLength(255)], nonNullable: true }),
      stages: new FormArray([
        new RoadOccupationStageForm()
      ])
    });
  }

  public get deviceType(): FormControl | null {
    return this.get('deviceType') as FormControl | null;
  }

  public createDeviceTypeControl(): void {
    this.addControl('deviceType', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public createWorkPurposeControl(): void {
    this.addControl('workPurpose', new FormControl('', {
      validators: [Validators.required, Validators.maxLength(5000)],
      nonNullable: true
    }));
  }

  public get workPurpose(): FormControl | null {
    return this.get('workPurpose') as FormControl | null;
  }

  public createSurfaceViolationControl(): void {
    this.addControl('surfaceViolation', new FormControl('', {
      validators: [Validators.required, Validators.maxLength(255)],
      nonNullable: true
    }));
  }

  public get surfaceViolation(): FormControl | null {
    return this.get('surfaceViolation') as FormControl | null;
  }

  public get occupationType(): FormControl | null {
    return this.get('occupationType') as FormControl | null;
  }

  public get purpose(): FormControl | null {
    return this.get('purpose') as FormControl | null;
  }

  public createDecisionControls(): void {
    this.addControl('locationDecisionNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      })
    );
    this.addControl('locationDecisionDate',
      new FormControl(null, {
        validators: [CustomValidator.isValidDateFormat(), CustomValidator.maxDate(this.locationDecisionMaxDate), Validators.required]
      })
    );
  }

  public showDecisionControls(): boolean {
    return !!this.locationDecisionNumber && !!this.locationDecisionDate;
  }

  private get locationDecisionNumber(): FormControl | null {
    return this.get('locationDecisionNumber') as FormControl | null;
  }

  private get locationDecisionDate(): FormControl | null {
    return this.get('locationDecisionDate') as FormControl | null;
  }

  public createAgreementControls(): void {
    this.addControl(
      'agreementNumber',
      new FormControl(
        '',
        {
          validators: [
            Validators.required,
            Validators.maxLength(255)
          ]
        }
      )
    );
    this.addControl('agreementDate',
      new FormControl(null, {
        validators: [
          CustomValidator.isValidDateFormat(),
          CustomValidator.maxDate(this.locationDecisionMaxDate),
          Validators.required
        ]
      })
    );
  }

  private get agreementNumber(): FormControl | null {
    return this.get('agreementNumber') as FormControl | null;
  }

  private get agreementDate(): FormControl | null {
    return this.get('agreementDate') as FormControl | null;
  }

  public showAgreementControls(): boolean {
    return !!this.agreementDate && !!this.agreementNumber;
  }

  public addStage(): void {
    this.stages.push(new RoadOccupationStageForm());
  }

  public removeStage(i: number): void {
    this.stages.removeAt(i);
  }

  public get stages(): FormArray {
    return this.get('stages') as FormArray;
  }

  public getStageForms(): RoadOccupationStageForm[] {
    return this.stages.controls as RoadOccupationStageForm[];
  }

  public override get valid(): boolean {
    return super.valid && !!this.stages.controls.map(c => c.valid).filter(v => v).length;
  }

  public get values(): RoadOccupationFormValue {
    return {
      ...this.value,
      stages: this.getStageForms().map(v => v.values)
    } as RoadOccupationFormValue;
  }

  public override patchFormValues(value: RoadOccupationFormValue): void {
    super.patchFormValues(value);
    this.stages.clear();
    (value?.stages || []).forEach(stage => {
      const sf = new RoadOccupationStageForm();
      sf.patchFormValues(stage);
      this.stages.push(sf);
    });
  }
}
